import React, { useEffect, useState } from "react";
import { api, toastError } from "../../../lib/api";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { AlertCircle, Building2, Building, Users } from "lucide-react";

/**
 * Scope-picker for Step 1 of the import wizard.
 *
 * Backend-authoritative: reads /api/import/my-scopes and never infers import
 * authority from the tenant membership directory. The parent's `value` is
 * whichever scope entry was picked; when empty, the parent must block
 * "Create import".
 */
export type ImportScopeEntry = {
  scope_type: "tenant" | "branch" | "team";
  membership_id: string | null;
  company_id: string | null;
  company_name: string | null;
  branch_id: string | null;
  branch_name: string | null;
  team_id: string | null;
  team_name: string | null;
  can_create_import: boolean;
};

export type MyScopesResponse = {
  can_create_import: boolean;
  scopes: ImportScopeEntry[];
};

export function scopeKey(s: ImportScopeEntry): string {
  if (s.scope_type === "tenant") return "tenant";
  if (s.scope_type === "branch") return `branch:${s.branch_id}`;
  return `team:${s.team_id}`;
}

export function scopeLabel(s: ImportScopeEntry): string {
  if (s.scope_type === "tenant") return "Tenant-wide";
  if (s.scope_type === "branch") {
    const parts = [s.company_name, s.branch_name].filter(Boolean);
    return `Branch · ${parts.join(" / ") || s.branch_id?.slice(0, 8)}`;
  }
  return `Team · ${s.team_name || s.team_id?.slice(0, 8)}`;
}

export function scopeIcon(s: ImportScopeEntry) {
  if (s.scope_type === "tenant") return Building2;
  if (s.scope_type === "branch") return Building;
  return Users;
}

export function ScopePicker({
  value,
  onChange,
}: {
  value: ImportScopeEntry | null;
  onChange: (s: ImportScopeEntry | null) => void;
}) {
  const [scopes, setScopes] = useState<ImportScopeEntry[]>([]);
  const [canCreate, setCanCreate] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<MyScopesResponse>("/import/my-scopes");
        if (cancelled) return;
        setScopes(data.scopes || []);
        setCanCreate(!!data.can_create_import);
        if (data.scopes?.length === 1) onChange(data.scopes[0]);
      } catch (e) {
        if (!cancelled) toastError(e, "Could not load your import scopes.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="text-sm text-slate-500" data-testid="scope-picker-loading">
        Loading your authorized scopes…
      </div>
    );
  }

  if (canCreate === false || scopes.length === 0) {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        data-testid="scope-picker-empty"
      >
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-medium">No import scope available</div>
          <div className="text-amber-800/80 mt-0.5">
            Your account does not have permission to create a contact import.
            Ask an Owner or Admin, or request a branch/team membership.
          </div>
        </div>
      </div>
    );
  }

  const selectedKey = value ? scopeKey(value) : "";
  const byKey = new Map(scopes.map((s) => [scopeKey(s), s]));

  return (
    <div className="space-y-1.5" data-testid="scope-picker">
      <Label htmlFor="scope-picker-select">Scope</Label>
      <Select
        value={selectedKey}
        onValueChange={(k) => onChange(byKey.get(k) ?? null)}
      >
        <SelectTrigger id="scope-picker-select" data-testid="scope-picker-trigger">
          <SelectValue placeholder="Choose an authorized scope" />
        </SelectTrigger>
        <SelectContent data-testid="scope-picker-content">
          {scopes.map((s) => {
            const Icon = scopeIcon(s);
            return (
              <SelectItem
                key={scopeKey(s)}
                value={scopeKey(s)}
                data-testid={`scope-option-${scopeKey(s)}`}
              >
                <span className="inline-flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 text-slate-500" />
                  {scopeLabel(s)}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <p className="text-xs text-slate-500">
        Contacts imported under this scope will be visible to users authorized on the same scope.
      </p>
    </div>
  );
}

/** Serialize the picked scope to the shape POST /import/jobs expects. */
export function toCreateJobScope(s: ImportScopeEntry): Record<string, string> {
  const out: Record<string, string> = { scope_type: s.scope_type };
  if (s.company_id) out.company_id = s.company_id;
  if (s.branch_id) out.branch_id = s.branch_id;
  if (s.team_id) out.team_id = s.team_id;
  if (s.membership_id) out.membership_id = s.membership_id;
  return out;
}
